import { Box, Button, makeStyles, Table, TableBody, TableCell, TableRow, Typography } from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import clsx from "clsx";
import { useState } from "react";
import { ProductStyles, useSharedStyles } from "../../../../common/sharedStyles";
import { riskOptions, withdrawTimeframes } from "../../../../common/types";
import { useSatSteps } from "../../../../hooks";
import Popup from "../../../containers/Popup";
import { Colors, HelpTooltip, SubtitleText, TitleText } from "../../../controls/helpers";
import { Values } from "../../ControlledForm";
import { Body, Hero } from "../../GridLayout";
import QuestionnaireScreen from "../../QuestionnaireScreen";
import { usePlanningToWithdraw, useRiskProfile, useWithdrawTimeframe } from "./BeginQuestions";
import { usePlanningToWithdrawWb, useWbWithdrawTimeframe } from "./WealthBuilder";

const percentages: any = {
  [riskOptions.low]: {
    1: { income: 100, inflation: 0 },
    2: { income: 100, inflation: 0 },
    3: { income: 100, inflation: 0 },
    4: { income: 100, inflation: 0 },
    5: { income: 100, inflation: 0 },
  },
  [riskOptions.med]: {
    1: { income: 100, inflation: 0 },
    2: { income: 80, inflation: 20 },
    3: { income: 70, inflation: 30 },
    4: { income: 60, inflation: 40 },
    5: { income: 60, inflation: 40 },
  },
  [riskOptions.high]: {
    1: { income: 100, inflation: 0 },
    2: { income: 80, inflation: 20 },
    3: { income: 70, inflation: 30 },
    4: { income: 60, inflation: 40 },
    5: { income: 50, inflation: 50 },
  },
};

const strategyDescriptions = {
  income: "Objective: To provide exposure to income assets, anticipated to mainly hold New Zealand, Australian and international bonds.",
  inflation: "Objective: To mitigate the impact of inflation on your investment over the medium/long term, anticipated to mainly hold New Zealand, Australian and international bonds and shares.",
  growth: "Objective: To grow your investment over the long term, anticipated to mainly hold New Zealand, Australian and international shares, and/or hedge funds.",
};

export const getAssetAllocationPercentages = (isKs?: boolean) => {
  const riskProfile = useRiskProfile();
  const timeframe = isKs ? useWithdrawTimeframe() : useWbWithdrawTimeframe();
  const withdrawTimeframe = withdrawTimeframes.find((t) => t.label === timeframe)?.years || 1;
  return {
    income: percentages[riskProfile][withdrawTimeframe].income,
    inflation: percentages[riskProfile][withdrawTimeframe].inflation,
    growth: 0,
  };
};

const SelfSelect = () => {
  const { currentStep } = useSatSteps();
  const isKs = currentStep.route.toLowerCase().indexOf("ks") !== -1;
  const classes = useStyles({ isKs });
  const sharedClasses = useSharedStyles();
  const riskProfile = useRiskProfile();
  const isWithdrawing = isKs ? usePlanningToWithdraw() : usePlanningToWithdrawWb();
  const assetAllocations = getAssetAllocationPercentages(isKs);
  const timeframe = isKs ? useWithdrawTimeframe() : useWbWithdrawTimeframe();
  const withdrawTimeframe = withdrawTimeframes.find((t) => t.label === timeframe)?.years || 1;
  const [popupShown, setPopupShown] = useState(false);
  const [nextPageRoute, setNextPageRoute] = useState(isKs ? "kiwisaver-calculator" : "contribution-calculator");

  const serializeResponses = (values: Values): Record<string, string> => {
    if (isKs) return { "Asset allocation": `${assetAllocations.income},${assetAllocations.inflation},${assetAllocations.growth}` };
    // might be needed in advice confirmation
    else return { "Wealth Builder asset allocation": `${assetAllocations.income},${assetAllocations.inflation},${assetAllocations.growth}` };
  };

  return (
    <QuestionnaireScreen serializeResponses={serializeResponses} overrideNextPageRoute={() => nextPageRoute}>
      <Hero isNarrowView className={classes.hero}>
        <TitleText>{isKs ? "KiwiSaver" : "Wealth Builder"}</TitleText>
      </Hero>
      <Body isNarrowView>
        <TitleText>
          The following asset allocation is a great option for those{" "}
          {!isWithdrawing && riskProfile === riskOptions.low
            ? "who are cautious investors"
            : `looking to withdraw their ${isKs ? "KiwiSaver" : "Wealth Builder"} in the next ${withdrawTimeframe} years`}
          .
        </TitleText>
        {!popupShown && !isWithdrawing && riskProfile === riskOptions.low && (
          <Popup onOkay={() => setPopupShown(true)}>
            NZ Funds Life Cycle is the standard recommendation for an investor who does not plan on withdrawing in the next five years. NZ Funds Life Cycle would typically hold a higher weight to
            growth assets, like shares. Growth assets tend to go up and down more in the short term than income assets like cash and bonds, but over the long term they have more potential for growth.
            <br />
            <br />
            As you have indicated you are a cautious investor, a more conservative mix of assets with a higher weighting to income assets may be more appropriate. Your adviser or nominated
            representative will confirm this.
          </Popup>
        )}
        <br />
        <TitleText hideMarginBottom>Asset allocation</TitleText>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell colSpan={2} className={classes.tableCell} style={{ padding: 0 }}>
                &nbsp;
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={clsx(classes.tableCell, sharedClasses.text, sharedClasses.black)}>
                <Typography style={{ display: "flex" }}>
                  Income Strategy
                  <HelpTooltip title={strategyDescriptions.income} />
                </Typography>
              </TableCell>
              <TableCell align="right" className={classes.tableCell}>
                <Typography>{assetAllocations.income}%</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={clsx(classes.tableCell, sharedClasses.text, sharedClasses.black)}>
                <Typography style={{ display: "flex" }}>
                  Inflation Strategy
                  <HelpTooltip title={strategyDescriptions.inflation} />
                </Typography>
              </TableCell>
              <TableCell align="right" className={classes.tableCell}>
                <Typography>{assetAllocations.inflation}%</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={clsx(classes.tableCell, sharedClasses.text, sharedClasses.black)}>
                <Typography style={{ display: "flex" }}>
                  Growth Strategy
                  <HelpTooltip title={strategyDescriptions.growth} />
                </Typography>
              </TableCell>
              <TableCell align="right" className={classes.tableCell}>
                <Typography>{assetAllocations.growth}%</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={clsx(classes.tableCell, classes.borderTop, sharedClasses.text, sharedClasses.black)}>
                <Typography>
                  <b>Total</b>
                </Typography>
              </TableCell>
              <TableCell align="right" className={clsx(classes.tableCell, classes.borderTop, sharedClasses.text, sharedClasses.black)}>
                <Typography>
                  <b>100%</b>
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Box py={4} textAlign="center">
          <SubtitleText>This asset allocation will be reviewed by your adviser or nominated representative.</SubtitleText>
          <Button type="submit" onClick={(_) => setNextPageRoute(isKs ? "ks-asset-allocation-options" : "wb-asset-allocation-options")} variant="contained" endIcon={<ArrowForwardIcon />}>
            Learn about other allocation options
          </Button>
        </Box>
      </Body>
    </QuestionnaireScreen>
  );
};

const useStyles = makeStyles(
  {
    tableCell: {
      borderTop: "0px solid transparent",
      borderBottom: "0px solid transparent",
      padding: 10,
      margin: 0,
    },
    borderTop: {
      borderTop: `1px solid ${Colors.Grey}`,
    },
    hero: ({ isKs }: { isKs: boolean }) => ({
      justifyContent: "flex-start !important",
      height: "144px !important",
      backgroundColor: `${isKs ? ProductStyles.KiwiSaver.color : ProductStyles.WealthBuilder.color} !important`,
      alignItems: "normal !important",
      padding: 24,
      "& h4": {
        color: `${Colors.White} !important`,
        lineHeight: 1,
      },
    }),
  },
  { name: "SelfSelect" }
);

export default SelfSelect;
